import React, { useState } from 'react';
import {
  PropertyFilterInput,
  PropertyConsumerCategory,
  ActionPropertyValueFragment,
  usePropertyDescriptionsQuery,
  ActionPropertyDescriptionFragment,
  SocketNotification,
  AttendeeActionPropertiesFragment,
  ConversationWithAttendeePropertiesFragment,
  AttendeeWithPropertiesFragment,
} from '$/GraphQL/schema/schema';
import type { ActionPropertiesHash } from '$/shared/typesProxy';
import { isEqualDeep, useHasChangedShallow } from '$/hooks/useHasChanged';
import { ConversationWithMessage } from '../Chat/hooks/useGetConversation';
import MessageEvent from '$/components/Socket/MessageEvent';
import { actionPropertiesHashFromArray } from '../Chat/actionPropertiesHashFromArray';

interface PropertiesContextValue {
  skip: boolean;
  loading: boolean;
  properties: ActionPropertyDescriptionFragment[];

  startFetch: () => void;
  refetch: () => Promise<void>;
}

export interface PropertiesWithHash {
  properties: ActionPropertyValueFragment[];
  propertiesHash: ActionPropertiesHash;
}

interface AttendeePropertiesContextValue {
  attendee?: AttendeeWithPropertiesFragment | ConversationWithMessage['attendee'];
  mentionField: PropertiesWithHash;
  conditionalDisplay: PropertiesWithHash;
  updateProperties: (properties: AttendeeActionPropertiesFragment) => void;
}

interface WebinarPropertiesContextValue {
  mentionField: PropertiesContextValue;
  conditionalDisplay: PropertiesContextValue;
  infoDialog: PropertiesContextValue;
  refetch: () => Promise<void>;
}

const useSkip = (): [boolean, () => void] => {
  const [skip, setSkip] = useState(true);
  const startFetch = React.useCallback(() => {
    if (!skip) return;
    setSkip(false);
  }, [skip]);
  return [skip, startFetch];
};

const useProperties = (filter: PropertyFilterInput): PropertiesContextValue => {
  const [skip, startFetch] = useSkip();
  const propertiesQuery = usePropertyDescriptionsQuery({
    variables: {
      filter: {
        ...filter,
      },
    },
    skip,
    notifyOnNetworkStatusChange: true,
  });

  const refetch = React.useCallback(async (): Promise<void> => {
    if (skip) return;
    await propertiesQuery.refetch();
  }, [propertiesQuery, skip]);

  const value = React.useMemo(
    () => ({
      skip,
      refetch,
      startFetch,
      loading: propertiesQuery.loading,
      properties: propertiesQuery.data?.propertyDescriptions ?? [],
    }),
    [propertiesQuery.data?.propertyDescriptions, propertiesQuery.loading, refetch, skip, startFetch]
  );

  return value;
};

const TeamPropertiesContext = React.createContext({} as PropertiesContextValue);
const WebinarPropertiesContext = React.createContext({} as WebinarPropertiesContextValue);
const AttendeePropertiesContext = React.createContext({} as AttendeePropertiesContextValue);

export const TeamPropertiesProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const value = useProperties({
    consumer: {
      category: PropertyConsumerCategory.Integration,
    },
    ewebinar: {
      includeDrafts: true,
      showInWebinars: true,
    },
  });

  return <TeamPropertiesContext.Provider value={value}>{children}</TeamPropertiesContext.Provider>;
};

export const WebinarPropertiesProvider: React.FC<{
  filter: Omit<PropertyFilterInput, 'consumer'>;
  children: React.ReactNode;
}> = ({ filter, children }) => {
  const { refetch: refetchTeamProperties } = React.useContext(TeamPropertiesContext);

  const conditionalDisplay = useProperties({
    ...filter,
    consumer: {
      category: PropertyConsumerCategory.ConditionalDisplay,
    },
  });

  const mentionField = useProperties({
    ...filter,
    consumer: {
      category: PropertyConsumerCategory.MentionField,
    },
  });

  const infoDialog = useProperties({
    ...filter,
    consumer: {
      category: PropertyConsumerCategory.InfoDialog,
    },
  });

  const refetch = React.useCallback(async (): Promise<void> => {
    await Promise.all([
      conditionalDisplay.refetch(),
      mentionField.refetch(),
      infoDialog.refetch(),
      refetchTeamProperties(),
    ]);
  }, [conditionalDisplay, mentionField, infoDialog, refetchTeamProperties]);

  const value = React.useMemo(
    () => ({
      conditionalDisplay,
      mentionField,
      infoDialog,
      refetch,
    }),
    [conditionalDisplay, mentionField, infoDialog, refetch]
  );

  return (
    <WebinarPropertiesContext.Provider value={value}>{children}</WebinarPropertiesContext.Provider>
  );
};

export const useRefetchWebinarPropertiesContext = (): (() => Promise<void>) => {
  const context = React.useContext(WebinarPropertiesContext);
  return context.refetch;
};

const usePropertiesContextValue = (context: PropertiesContextValue): PropertiesContextValue => {
  const ctx = context;
  const startFetch = ctx?.startFetch;

  React.useEffect(() => {
    startFetch?.();
  }, [startFetch]);
  return context;
};

export const useConditionalDisplayPropertiesContext = (): PropertiesContextValue => {
  const context = React.useContext(WebinarPropertiesContext).conditionalDisplay;
  return usePropertiesContextValue(context);
};

export const useMentionFieldPropertiesContext = (): PropertiesContextValue => {
  const context = React.useContext(WebinarPropertiesContext).mentionField;
  return usePropertiesContextValue(context);
};

export const useTeamPropertiesContext = (): PropertiesContextValue => {
  const context = React.useContext(TeamPropertiesContext);
  return usePropertiesContextValue(context);
};

export const useRegistrantInfoDialogPropertiesContext = (): PropertiesContextValue => {
  const context = React.useContext(WebinarPropertiesContext).infoDialog;
  return usePropertiesContextValue(context);
};

export const AttendeePropertiesProvider: React.FC<{
  attendee?:
    | AttendeeWithPropertiesFragment
    | Omit<ConversationWithAttendeePropertiesFragment['attendee'], 'urls'>;
  children: React.ReactNode;
}> = ({ attendee, children }) => {
  const [properties, setProperties] = React.useState<{
    mentionField: ActionPropertyValueFragment[];
    conditionalDisplay: ActionPropertyValueFragment[];
  }>({
    mentionField: attendee?.properties?.mentionField ?? [],
    conditionalDisplay: attendee?.properties?.conditionalDisplay ?? [],
  });

  const updateProperties = React.useCallback(
    (newProperties: AttendeeActionPropertiesFragment): void => {
      setProperties((oldProperties) => ({
        mentionField: [...oldProperties.mentionField, ...(newProperties.mentionField ?? [])],
        conditionalDisplay: [
          ...oldProperties.conditionalDisplay,
          ...(newProperties.conditionalDisplay ?? []),
        ],
      }));
    },
    []
  );

  const hasActionPropertyValueChanged = useHasChangedShallow([attendee?.properties]);
  React.useEffect(() => {
    if (!hasActionPropertyValueChanged) return;
    if (!attendee?.properties) return;

    const nextProperties = {
      mentionField: attendee?.properties?.mentionField ?? [],
      conditionalDisplay: attendee?.properties?.conditionalDisplay ?? [],
    };
    if (!isEqualDeep(nextProperties, properties)) {
      setProperties({
        mentionField: attendee?.properties?.mentionField ?? [],
        conditionalDisplay: attendee?.properties?.conditionalDisplay ?? [],
      });
    }
  }, [attendee?.properties, hasActionPropertyValueChanged, setProperties, properties]);

  const messageHandler = React.useCallback(
    (message: SocketNotification) => {
      if (
        message.chatMessage?.attendee?.id !== attendee?.id ||
        !message.chatMessage?.attendee?.actionProperties
      )
        return;
      updateProperties({
        mentionField: message.chatMessage?.attendee?.actionProperties ?? [],
      });
    },
    [attendee?.id, updateProperties]
  );

  const value: AttendeePropertiesContextValue = React.useMemo(
    () => ({
      mentionField: {
        properties: properties.mentionField,
        propertiesHash: actionPropertiesHashFromArray(properties.mentionField),
      },
      conditionalDisplay: {
        properties: properties.conditionalDisplay,
        propertiesHash: actionPropertiesHashFromArray(properties.conditionalDisplay),
      },
      updateProperties,
      attendee,
    }),
    [properties, attendee, updateProperties]
  );

  return (
    <AttendeePropertiesContext.Provider value={value}>
      {children}
      <MessageEvent handler={messageHandler} />
    </AttendeePropertiesContext.Provider>
  );
};

export const useAttendeePropertiesContext = (): AttendeePropertiesContextValue => {
  return React.useContext(AttendeePropertiesContext);
};
